import { useRef, Fragment, type FC } from 'react';
import { graphql, useFragment } from 'react-relay';

import { SharedItemTile } from '../../shared/SharedItemTile/SharedItemTile.tsx';
import { FavoritesProvider } from '../../components/global/FavoriteIcons/FavoritesProvider.tsx';
import { QuickViewProvider } from 'dibs-buyer-product-tile/exports/QuickView.ts';
import { BuyerProductTileShimmer } from 'dibs-buyer-product-tile/exports/BuyerProductTileShimmer.ts';
import { useBuyerProductTileSize } from '../../shared/hooks/useBuyerProductTileSize.ts';
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext.tsx';
import { useSharedUrgencySignals } from '../../finding/hooks/sharedUrgencySignals/useSharedUrgencySignals.ts';
import { useItemShippingPrices } from '../../finding/hooks/useItemShippingPrices.ts';
import { useSbSharedItemTracking } from '../../finding/hooks/sharedItemTracking/useSbSharedItemTracking.ts';
import { usePartialItems } from '../../finding/hooks/usePartialItems.ts';
import { useSbSelector } from '../../reducers/useSbSelector.ts';
import { useClientState } from 'dibs-react-hooks/exports/useClientState.ts';
import { getIsOpenPdpInNewTabVariant } from '../../utils/abTestHelper.ts';
import { SbSharedPartialItemsPlaceholder } from '../../finding/SbSharedPartialItemsPlaceholder/SbSharedPartialItemsPlaceholder.tsx';

import {
    VERTICAL_OFFSET_SB_RESP_IMG,
    VERTICAL_OFFSET_MOBILE_IMG,
} from '../../constants/imageConstants.js';
import { locations } from '../../utils/tracking/shared/favoritesTracking.tsx';
import { filterFalsy } from 'dibs-ts-utils/exports/filterFalsy.ts';

import { type FavoritesProviderChildrenProps } from '../../components/global/FavoriteIcons/FavoritesProviderChildrenProps.ts';
import { type BuySharedResultContainer_viewer$key } from './__generated__/BuySharedResultContainer_viewer.graphql.ts';
import { type BuySharedResultContainer_buyPage$key } from './__generated__/BuySharedResultContainer_buyPage.graphql.ts';
import { type BuySharedResultContainer_user$key } from './__generated__/BuySharedResultContainer_user.graphql.ts';

import classnames from 'classnames';
import styles from './BuySharedResultContainer.scss';

const LAZY_LOAD_ITEM_ROWS = 3;

const viewerFragment = graphql`
    fragment BuySharedResultContainer_viewer on Viewer {
        ...SharedItemTile_viewer
        ...useSbSharedItemTracking_viewer
        ...usePartialItems_viewer
    }
`;
const buyPageFragment = graphql`
    fragment BuySharedResultContainer_buyPage on ItemSearchQueryConnection {
        ...SharedItemTile_itemSearch
        ...SbSharedProductAdWrapper_itemSearch
        edges {
            node {
                item {
                    serviceId
                    ...usePartialItems_item
                    ...FavoritesProvider_item
                    ...useSharedUrgencySignals_item
                    ...useItemShippingPrices_item
                    ...useSbSharedItemTracking_item
                    ...SharedItemTile_item
                        @arguments(
                            isTrade: $isTrade
                            fetchVideo: $fetchTileVideo
                            priceBookName: $priceBookName
                            showSeller: $showSeller
                        )
                }
            }
        }
        ...useSbSharedItemTracking_itemSearch
    }
`;
const userFragment = graphql`
    fragment BuySharedResultContainer_user on User {
        ...SharedItemTile_user
    }
`;

type Props = {
    viewer: BuySharedResultContainer_viewer$key;
    buyPage: BuySharedResultContainer_buyPage$key;
    user: BuySharedResultContainer_user$key;
    shouldShowRefineMenu: boolean;
};

export const BuySharedResultContainer: FC<Props> = ({
    viewer: viewerRef,
    buyPage: buyPageRef,
    user: userRef,
    shouldShowRefineMenu,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const buyPage = useFragment(buyPageFragment, buyPageRef);
    const user = useFragment(userFragment, userRef);
    const items = (buyPage?.edges || []).map(edge => edge?.node?.item).filter(filterFalsy);
    const {
        fetchPartialItems,
        numOfInflatedItems,
        numOfPlaceholderItems,
        getPartialItemTrackingData,
    } = usePartialItems(items, viewer);

    const { fetchItemUrgencySignals, getUrgencySignal } = useSharedUrgencySignals(items);
    const { fetchItemShippingPrices, getItemShippingPrices, getItemShippingTrackingData } =
        useItemShippingPrices(items);
    const { fireItemImpressionTracking, fireItemClickTracking } = useSbSharedItemTracking({
        viewer,
        itemSearch: buyPage,
        items,
        getUrgencySignal,
        getItemShippingPrices,
        getItemShippingTrackingData,
        getPartialItemTrackingData,
    });
    const showMeasurements = useSbSelector(state => state.displayPreferences.showMeasurements);
    const refetchInFlight = useSbSelector(state => state.filters.refetchInFlight);
    const { afterDisplay, userId } = useSbSelector(state => state.relayVariables.variables);
    const { isMobile } = useServerVarsContext();
    const tileSize = useBuyerProductTileSize();
    const gridContainerRef = useRef<HTMLDivElement>(null);

    const isClient = useClientState();
    const isOpenPdpInNewTabVariant = isClient && getIsOpenPdpInNewTabVariant();

    // if refine menu is shown we mustn't show more than 3 items per row due to spacing
    const itemsPerRow = shouldShowRefineMenu ? 3 : 4;

    const gridContainerClassName = classnames(styles.gridContainer, {
        [styles.gridContainer2ItemsRow]: isMobile,
        [styles.gridContainer4ItemsRow]: !isMobile && itemsPerRow === 4,
        [styles.gridContainerAdaptiveItemsRow]: !isMobile && itemsPerRow !== 4,
    });

    return (
        <div className={gridContainerClassName} ref={gridContainerRef}>
            <FavoritesProvider
                disable={!afterDisplay}
                items={items}
                // location should be kept the same GROWTH-4297
                location={locations.SEARCH_BROWSE}
                userId={userId}
                fetchFolder={!isMobile}
            >
                {({ props: favorites }: { props: FavoritesProviderChildrenProps }) => (
                    <QuickViewProvider elementRef={gridContainerRef}>
                        {Array.from({ length: numOfInflatedItems }, (_, index) => {
                            const item = items[index];
                            if (refetchInFlight && !isMobile) {
                                return (
                                    <BuyerProductTileShimmer
                                        key={item?.serviceId}
                                        size={tileSize}
                                    />
                                );
                            }
                            return (
                                <Fragment key={item?.serviceId}>
                                    <SharedItemTile
                                        showMeasurements={showMeasurements}
                                        key={item?.serviceId}
                                        item={item}
                                        user={user}
                                        favorites={favorites}
                                        index={index}
                                        isOpenPdpInNewTabVariant={isOpenPdpInNewTabVariant}
                                        lazyLoadStartIndex={
                                            isMobile ? undefined : LAZY_LOAD_ITEM_ROWS * itemsPerRow
                                        }
                                        imageLoadVerticalOffset={
                                            isMobile
                                                ? VERTICAL_OFFSET_MOBILE_IMG
                                                : VERTICAL_OFFSET_SB_RESP_IMG
                                        }
                                        beforeRender={() => {
                                            fetchItemUrgencySignals(index);
                                            fetchItemShippingPrices(index);
                                        }}
                                        onContentVisible={() =>
                                            fireItemImpressionTracking({
                                                itemId: item?.serviceId,
                                                index,
                                            })
                                        }
                                        onClick={() =>
                                            fireItemClickTracking({
                                                itemId: item?.serviceId,
                                                index,
                                            })
                                        }
                                        viewer={viewer}
                                        itemSearch={buyPage}
                                        fetchFolderOnMouseOver
                                        urgencySignal={getUrgencySignal(item.serviceId)?.message}
                                        shippingPrices={getItemShippingPrices(item.serviceId)}
                                    />
                                </Fragment>
                            );
                        })}
                        <SbSharedPartialItemsPlaceholder
                            numOfPlaceholderItems={numOfPlaceholderItems}
                            fetchPartialItems={fetchPartialItems}
                            numOfInflatedItems={numOfInflatedItems}
                            numOfAboveFoldRows={LAZY_LOAD_ITEM_ROWS}
                            itemsPerRow={!isMobile ? itemsPerRow : undefined}
                        />
                    </QuickViewProvider>
                )}
            </FavoritesProvider>
        </div>
    );
};
